
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {createOctreeRoiQuery} from "../geometry/pipeRoiAdapter.js";
import {fitPlaneFromPoints} from "../geometry/localPlaneFit.js";

const THROTTLE_MS = 120;          // matches pipeTraceTool.js's own "throttle text" cadence
const QUERY_RADIUS_MARGIN = 1.3;  // query a bit wider than the figure's own current size
const MIN_AXIAL_HALF_LENGTH = 0.3;
const MAX_AXIAL_HALF_LENGTH = 1.5;
const WORLD_UP = new THREE.Vector3(0, 0, 1);

function now(){ return typeof performance !== "undefined" ? performance.now() : Date.now(); }

// Shared point-cloud-driven plane estimator for Cirkel/Rektangel's dynamic
// capture phase (task: "punktskyen skal bruges til løbende at estimere et
// passende lokalt 3D-plan"). Owns ONE octree ROI query for the whole
// capture session - reusing pipeRoiAdapter.js's existing cached/voxel-
// gridded query (see that file's own doc comment: "ONE octree-ROI code
// path in the project, not two") rather than a second point-cloud-query
// implementation - and throttles/serialises the expensive part (the ROI
// query + PCA refit) so a fast mousemove stream never queues up
// overlapping queries.
//
// The cheap part - regenerating the exact figure from the CURRENT
// best-known plane - runs on every mousemove regardless (see circleTool.js/
// rectangleTool.js's own redraw()), so the preview itself never blocks on
// point-cloud I/O; only the figure's ORIENTATION lags behind by up to one
// throttle interval while a fresh query is in flight.
//
// Construction failure (e.g. a pointcloud with a non-translation transform
// - see pipeRoiAdapter.js's assertTranslationOnlyTransform) is caught here,
// not left to crash the capture: `poke()` then simply never manages to
// start a query, so `currentPlane()` keeps returning the stable fallback
// plane for the whole session - the same "stabil fallback ved
// utilstrækkelig punktsky-support" path as "not enough points nearby".
export function createLivePlaneEstimator(dm){
	let roi = null;
	try {
		roi = createOctreeRoiQuery(dm.viewer.scene.pointclouds);
	} catch(e){
		roi = null;
	}

	let lastFit = null;
	let lastQueryAt = 0;
	let inFlight = false;
	let disposed = false;

	function currentPlane(fallbackOrigin, fallbackNormal){
		if(lastFit) return lastFit;
		return fitPlaneFromPoints([], {fallbackOrigin, fallbackNormal});
	}

	// center/radialExtent: world-space query anchor + the figure's own
	// current rough size (always available immediately from the raw,
	// mouse-driven parametric shape - unlike the plane fit itself).
	// fallbackOrigin/fallbackNormal: passed straight through to
	// fitPlaneFromPoints() for when support is insufficient.
	// onUpdated(fit): called asynchronously once a NEW plane is ready -
	// never synchronously, and never after dispose().
	function poke(center, radialExtent, fallbackOrigin, fallbackNormal, onUpdated){
		if(!roi || disposed || inFlight) return;
		const t = now();
		if(t - lastQueryAt < THROTTLE_MS) return;
		lastQueryAt = t;
		inFlight = true;

		const radialQuery = Math.max(0.1, radialExtent * QUERY_RADIUS_MARGIN);
		const axialHalfLength = Math.min(MAX_AXIAL_HALF_LENGTH, Math.max(MIN_AXIAL_HALF_LENGTH, radialExtent * 0.4));

		roi.queryPoints(center.clone(), WORLD_UP, radialQuery, axialHalfLength)
			.then((points) => {
				inFlight = false;
				if(disposed) return;
				const plain = points.map((p) => ({x: p.x, y: p.y, z: p.z}));
				const fit = fitPlaneFromPoints(plain, {
					previousNormal: lastFit && lastFit.ok ? lastFit.normal : null,
					fallbackOrigin: {x: fallbackOrigin.x, y: fallbackOrigin.y, z: fallbackOrigin.z},
					fallbackNormal: {x: fallbackNormal.x, y: fallbackNormal.y, z: fallbackNormal.z},
					queryRadius: radialQuery,
				});
				lastFit = fit;
				onUpdated(fit);
			})
			.catch(() => { inFlight = false; });
	}

	return {
		currentPlane,
		poke,
		dispose(){ disposed = true; if(roi) roi.dispose(); },
	};
}
